import { gql } from 'react-apollo';

export const getAssessmentGroupInfo = gql`
  query getAssessmentGroupInfo($id: Int!, $assignmentId: Int) {
    assessmentGroupInfo(id: $id, assignmentId: $assignmentId) {
      id
      name
      subject
      type
      semester
      members {
        partName
        partType
        sortOrder
        assessmentId
        unitMask
      }
      window {
        registrationStartDate
        registrationEndDate
        winterWindowStartDate
        winterWindowEndDate
        springWindowStartDate
        springWindowEndDate
      }
    }
  }
`;

export const shareResource = gql`
  mutation shareResourceWithClass(
    $subject: Int!
    $resourceId: String
    $resourceIds: [String]
    $assignees: [IsaAssignee]
    $resourceType: String
    $startsAt: DateTime
    $dueAt: DateTime
    $title: String
  ) {
    shareResourceWithClass(
      subjectId: $subject
      id: $resourceId
      ids: $resourceIds
      assignees: $assignees
      resourceType: $resourceType
      startsAt: $startsAt
      dueAt: $dueAt
      title: $title
    ) {
      ok
      errors {
        fieldName
        fieldErrors
      }
      newAssignmentsNumber
    }
  }
`;

export const getResourceAssigneesStudents = gql`
  query resourceQuery(
    $resourceId: String!
    $subjectId: Int!
    $classIds: [Int!]
  ) {
    resourceAssigneesStudents(
      resourceId: $resourceId
      subjectId: $subjectId
      classIds: $classIds
    ) {
      assignees {
        sectionId
        studentIds
      }
    }
  }
`;

export const getSharedResourceToClassStudents = gql`
  query resourceQuery($sharedResourceToClassIds: [Int!]) {
    sharedResourceToClassStudents(
      sharedResourceToClassIds: $sharedResourceToClassIds
    ) {
      assignments {
        currentIsa {
          assignmentId
          sectionId
          studentIds
        }
        studentIdsFromOtherIsa
      }
    }
  }
`;

// PDF Downloads
export const testBookletQuery = gql`
  query getTestBookletQuery($assignmentId: String!, $masterSubjectId: String!) {
    assignment(assignmentId: $assignmentId) {
      id
      testBookletUrl(masterSubjectId: $masterSubjectId)
    }
  }
`;

// Associated Resources
export const associatedResourcesQuery = gql`
  query getAssociatedResources(
    $assessmentId: String!
    $educationPeriod: String!
  ) {
    assessment(assessmentId: $assessmentId, educationPeriod: $educationPeriod) {
      associatedResources {
        resourceUrl
        resourceType
      }
    }
  }
`;

export const assignmentQuestionData = gql`
  query assignmentQuestionData($assignmentId: String!) {
    assignment(assignmentId: $assignmentId) {
      id
      title
      questions {
        ... on MultipleChoiceQuestion {
          id: itemId
          itemId
          title
          isSecure
          isRetired
          learnosityItemReference
          isCbPracticeQuiz
          type: __typename
        }
        ... on FreeResponseQuestion {
          id: itemId
          itemId
          title
          isSecure
          isRetired
          learnosityItemReference
          isCbPracticeQuiz
          type: __typename
          children: scoringRubric {
            id
            title
            learnosityItemReference
            type: __typename
          }
        }
      }
    }
  }
`;

export const assignmentReview = gql`
  query assignmentReview(
    $assignmentId: String!
    $studentId: String!
    $config: String
    $inline: Boolean
    $showCorrectAnswers: Boolean
    $assignmentIdAsInt: Int
    $subjectId: Int
    $htmlId: String
    $scorerType: String
  ) {
    assignmentReview(
      assignmentId: $assignmentId
      studentId: $studentId
      config: $config
      inline: $inline
      showCorrectAnswers: $showCorrectAnswers
      scorerType: "teacher"
    ) {
      learnositySignedRequest
      allowCalculatorForItems {
        itemId
        revisionId
        learnosityItemReference
      }
      scoringRubric {
        learnositySignedRequest
        rubricCategoryReferencesByQuestion
      }
      status
      scoreDisplay
    }
    assignment(assignmentId: $assignmentId) {
      id
      studentScoring
      studentResponses(studentId: $studentId)
      scoringRubricResponseAnalysis(
        studentId: $studentId
        scorerType: $scorerType
      )
      scoringRubricResponses(studentId: $studentId, scorerType: $scorerType)
      progress(userId: $studentId) {
        scored
        studentScored
        teacherAndStudentScored
      }
    }
    assessmentGroupByAssignmentInfo(
      assignmentId: $assignmentIdAsInt
      subjectId: $subjectId
      htmlId: $htmlId
    ) {
      type
    }
  }
`;

export const activityResultQuery = gql`
  query getActivityResultByGroup(
    $masterSubjectId: Int!
    $classId: Int!
    $educationPeriod: Int
  ) {
    activityResultByGroup(
      masterSubjectId: $masterSubjectId
      classId: $classId
      educationPeriod: $educationPeriod
    ) {
      activityResultByGroup
    }
  }
`;

export const updateResourceSettings = gql`
  mutation updateResourceSettings(
    $sharedResourceToClassId: String!
    $startsAt: DateTime
    $dueAt: DateTime
    $fieldChanged: String
    $newStudentIds: [String!]
  ) {
    updateResourceSettings(
      sharedResourceToClassId: $sharedResourceToClassId
      startsAt: $startsAt
      dueAt: $dueAt
      newStudentIds: $newStudentIds
      fieldChanged: $fieldChanged
    ) {
      ok
      errors {
        fieldName
        fieldErrors
      }
      startsAt
      dueAt
    }
  }
`;

export const getItemShareInviteInfoQuery = gql`
  query getItemShareInvite(
    $shareKey: String!
    $groupId: Int
    $masterSubjectId: Int!
  ) {
    itemShareInvites(shareKey: $shareKey, masterSubjectId: $masterSubjectId) {
      initId
      itemId
      itemSharedDate: createdAt
      createdBy
      shareKey
      itemShareId
      hasPendingCopy
      hasCopy
      isDisabled
      itemInfo {
        title
        description
        itemId
        lastModifiedDate
        isOwner
      }
      itemMetadata {
        numberOfQuestions
        numberOfMcq
        numberOfFrq
        numberOfTeacherQuestions
      }
      tags {
        units {
          description
          label
        }
        topics {
          label
          description
        }
        skills {
          label
          description
          category
        }
        taskModels {
          label
          description
          category
        }
        bigIdeas {
          label
          description
          category
        }
        proficiencies {
          label
          description
          category
        }
        courseTransferSkills {
          label
          description
          category
        }
      }
    }
    groupTimezone(groupId: $groupId)
  }
`;

export const getItemDescription = gql`
  query getItemDescription($assessmentId: String) {
    assessment(assessmentId: $assessmentId) {
      id: itemId
      description
    }
  }
`;

export const getAssessmentSummaryQuery = gql`
  query getAssessmentSummary($itemId: Int!, $groupId: Int) {
    assessmentSummary(itemId: $itemId) {
      itemId
      createdBy
      isTeacherCreated
      itemSharedDate
      itemInfo {
        itemId
        title
        description
        lastModifiedDate
        isOwner
        createdDate
      }
      itemMetadata {
        numberOfQuestions
        numberOfMcq
        numberOfFrq
        numberOfTeacherQuestions
      }
      tags {
        units {
          description
          label
        }
        topics {
          label
          description
        }
        skills {
          label
          description
          category
        }
        taskModels {
          label
          description
          category
        }
        bigIdeas {
          label
          description
          category
        }
        proficiencies {
          label
          description
          category
        }
        courseTransferSkills {
          label
          description
          category
        }
      }
    }
    groupTimezone(groupId: $groupId)
  }
`;

export const manageItemSharesByMeQuery = gql`
  query getItemShares(
    $masterSubjectId: Int!
    $itemId: Int!
    $isEmailShares: Boolean
  ) {
    itemSharesByMe(
      masterSubjectId: $masterSubjectId
      itemId: $itemId
      isEmailShares: $isEmailShares
    ) {
      owner {
        firstName
        lastName
        emailTrimmed
      }
      initId
      ownerId
      sharedAt
      disabledAt
    }
    itemShareInvite(itemId: $itemId) {
      initId
      createdAt
      shareKey
      disabledAt
    }
  }
`;

export const updateItemShareInviteActive = gql`
  mutation updateItemShareInviteActive($shareKey: String!, $active: Boolean!) {
    updateItemShareInviteActive(shareKey: $shareKey, active: $active) {
      ok
    }
  }
`;

export const batchUpdateItemShareActive = gql`
  mutation batchUpdateItemShareActive(
    $itemShareIds: [Int]!
    $active: Boolean!
  ) {
    batchUpdateItemShareActive(itemShareIds: $itemShareIds, active: $active) {
      ok
    }
  }
`;

export const isQuizSharedQuery = gql`
  query isQuizSharedQuery($assessmentId: String) {
    assessment(assessmentId: $assessmentId) {
      id: itemId
      isShared
    }
  }
`;
